<template>
  <div v-if="value" class="al-backdrop" @click.self="$emit('input', false)">
    <aside class="al-panel" role="dialog" aria-modal="true" aria-labelledby="al-title">
      <div class="al-head">
        <div>
          <h5 id="al-title" class="mb-0"><i class="fe fe-clock mr-1"></i> Activity log</h5>
          <small class="text-muted">{{ subtitle }}</small>
        </div>
        <button class="btn btn-sm btn-link text-secondary" @click="$emit('input', false)" aria-label="Close"><i class="fe fe-x"></i></button>
      </div>
      <div class="al-tools">
        <select class="form-control form-control-sm" v-model="what" style="width: auto;">
          <option value="">Everything</option>
          <option v-for="w in kinds" :key="w" :value="w">{{ w }}</option>
        </select>
        <button class="btn btn-sm btn-outline-secondary" @click="load" :disabled="loading"><i class="fe fe-refresh-cw"></i></button>
      </div>
      <div class="al-body">
        <div v-if="loading" class="text-center text-muted py-5"><span class="spinner-border spinner-border-sm mr-2"></span>Loading…</div>
        <div v-else-if="error" class="alert alert-danger small">{{ error }}</div>
        <div v-else-if="!shown.length" class="text-center text-muted py-5"><i class="fe fe-inbox d-block mb-2" style="font-size: 1.6rem;"></i>No activity recorded.</div>
        <ul v-else class="al-list">
          <li v-for="e in shown" :key="e.id" class="al-item">
            <span class="al-dot" :class="dotClass(e)"><i class="fe" :class="icon(e)"></i></span>
            <div class="al-content">
              <div class="d-flex flex-wrap align-items-baseline" style="gap: 6px;">
                <b>{{ title(e) }}</b>
                <span class="al-kind">{{ e.what }}</span>
              </div>
              <div class="small text-muted">
                {{ e.by ? e.by.name : "System" }}<span v-if="e.by && e.by.job_title"> · {{ e.by.job_title }}</span> · {{ e.at | moment("D MMM YYYY, h:mm a") }}
              </div>
              <div v-if="e.from || e.to" class="al-move small mt-1">
                <span>{{ e.from || "—" }}</span> <i class="fe fe-arrow-right mx-1"></i> <b>{{ e.to }}</b>
              </div>
              <div v-if="e.reason" class="al-reason small mt-1"><i class="fe fe-message-square mr-1"></i>{{ e.reason }}</div>
              <ul v-if="e.changes.length && !(e.from || e.to)" class="al-changes small mt-1">
                <li v-for="c in e.changes" :key="c.field">
                  <span class="text-muted">{{ c.label }}:</span>
                  <template v-if="e.action === 'created' || c.old === null"> {{ c.new }}</template>
                  <template v-else-if="c.new === null"> <s>{{ c.old }}</s></template>
                  <template v-else> <s class="text-muted">{{ c.old }}</s> <i class="fe fe-arrow-right mx-1"></i> {{ c.new }}</template>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </aside>
  </div>
</template>

<script>
/**
 * Slide-in activity timeline of an authorization code or a claim
 * (GET authorization_code/{id}/activity or claims/{id}/activity): who did what and when, with changed values.
 */
export default {
  name: "ActivityLogPanel",
  props: {
    value: { type: Boolean, default: false },
    type: { type: String, required: true }, // authorization_code | claim
    recordId: { type: [Number, String], required: true },
    subtitle: { type: String, default: "" },
  },
  data() {
    return { entries: [], loading: false, error: "", what: "" };
  },
  computed: {
    kinds() { return [...new Set(this.entries.map((e) => e.what))]; },
    shown() { return this.what ? this.entries.filter((e) => e.what === this.what) : this.entries; },
  },
  watch: {
    value(open) { if (open) this.load(); },
  },
  methods: {
    load() {
      this.loading = true;
      this.error = "";
      const url = this.type === "claim" ? `/api/v1/auth/claims/${this.recordId}/activity` : `/api/v1/auth/authorization_code/${this.recordId}/activity`;
      this.axios.get(url)
        .then((res) => { this.entries = res.data.data || []; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the activity log."; })
        .finally(() => { this.loading = false; });
    },
    title(e) {
      if (!["created", "updated", "deleted"].includes(e.action)) return e.action;
      const verbs = { created: "Created", updated: "Updated", deleted: "Deleted" };
      const status = e.changes.find((c) => c.field === "status");
      if (e.action === "updated" && status && status.new) return `Status changed to ${status.new}`;
      return `${verbs[e.action]} ${e.what.toLowerCase()}`;
    },
    icon(e) {
      if (e.from || e.to) return "fe-repeat";
      if (e.what === "Comment") return "fe-message-circle";
      return { created: "fe-plus", updated: "fe-edit-2", deleted: "fe-trash-2" }[e.action] || "fe-check";
    },
    dotClass(e) {
      if (e.from || e.to) return "dot-move";
      if (e.action === "deleted") return "dot-del";
      if (e.action === "created") return "dot-new";
      return "";
    },
  },
};
</script>

<style scoped>
.al-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.35); z-index: 1060; display: flex; justify-content: flex-end; }
.al-panel { background: #fff; width: 100%; max-width: 520px; height: 100%; display: flex; flex-direction: column; box-shadow: -10px 0 40px rgba(15, 23, 42, 0.2); }
.al-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 18px 20px 12px; border-bottom: 1px solid #e2e8f0; }
.al-tools { display: flex; gap: 8px; padding: 10px 20px; border-bottom: 1px solid #f1f5f9; }
.al-body { overflow-y: auto; padding: 16px 20px; flex: 1; }
.al-list { list-style: none; padding: 0; margin: 0; position: relative; }
.al-list::before { content: ""; position: absolute; left: 14px; top: 6px; bottom: 6px; width: 2px; background: #e2e8f0; }
.al-item { display: flex; gap: 12px; padding: 0 0 18px; position: relative; }
.al-dot { width: 30px; height: 30px; border-radius: 50%; background: #e0e7ff; color: #3730a3; display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 1; }
.al-dot.dot-new { background: #d1fae5; color: #065f46; }
.al-dot.dot-del { background: #fee2e2; color: #991b1b; }
.al-dot.dot-move { background: #ede9fe; color: #5b21b6; }
.al-content { flex: 1; min-width: 0; }
.al-kind { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; font-weight: 600; }
.al-changes { list-style: none; padding: 6px 10px; margin: 0; background: #f8fafc; border-radius: 8px; word-break: break-word; }
.al-move { background: #f5f3ff; border-radius: 8px; padding: 6px 10px; }
.al-reason { background: #fffbeb; border-radius: 8px; padding: 6px 10px; color: #92400e; }
@media (max-width: 576px) { .al-panel { max-width: none; } }
</style>
